import { FC, ComponentPropsWithoutRef, ReactNode, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Copy, Edit } from 'jbc-front/components/icons';
import { PaginatorWithResult } from 'jbc-front/components/SearchForm';
import { DescriptionContainer, HoverDescription } from 'jbc-front/components/presenters/ui/HoverDescription';
import { Content } from '@/components/layout/Content';
import { useDisplayEmployeeLimit } from '@/components/utils/display_employee_limit';
import { dateFormat, displayFormat } from '@/components/utils/formatter';
import { LoadingPage } from '@/components/LoadingPage';
import { Table } from '@/components/Table';
import { useQuery } from '@/hooks/graphql/useQuery';
import { SearchForm } from './parts/SearchForm';
import { DeleteReport } from './parts/DeleteReport';
import {
  REGULAR_HEALTH_CHECKUP_REPORTS,
  RegularHealthCheckupReports,
  RegularHealthCheckupReportsQueryVariables
} from './query';
import {
  convertFormToSearchParams,
  convertSearchParamsToForm,
  convertSearchParamsToGraphQL,
  convertSearchParamsToQueryString,
  getSearchParamsFromLocation,
  hasConditions
} from './search';
import styles from './SearchReports.scss';

const dateSpanFormat = (startOn: string, endOn: string) => {
  if (startOn && endOn) {
    return `${dateFormat(startOn, 'YYYY/M/D')} 〜 ${dateFormat(endOn, 'YYYY/M/D')}`;
  } else if (startOn) {
    return `${dateFormat(startOn, 'YYYY/M/D')} 〜`;
  } else if (endOn) {
    return `〜 ${dateFormat(endOn, 'YYYY/M/D')}`;
  }
  return displayFormat();
};

export const SearchReports: FC = () => {
  const location = useLocation();
  const history = useHistory();
  const [limit] = useDisplayEmployeeLimit();
  const searchParams = { ...getSearchParamsFromLocation(location), limit };

  const { data, loading, refetch } = useQuery<RegularHealthCheckupReports, RegularHealthCheckupReportsQueryVariables>(
    REGULAR_HEALTH_CHECKUP_REPORTS,
    {
      variables: convertSearchParamsToGraphQL(searchParams),
      fetchPolicy: 'network-only'
    }
  );

  return (
    <div>
      <div className="l-main-title-wrap">
        <h1 className="m-title-main">定期健康診断結果報告書一覧</h1>
      </div>
      <Content size="xl-4">
        <SearchForm
          values={convertSearchParamsToForm(searchParams)}
          totalCount={data?.client?.regularHealthCheckupReports?.totalCount}
          isLoading={loading}
          onSubmit={values => {
            const { _page, ...newSearchParams } = convertFormToSearchParams(values); // reset page
            history.push({ search: convertSearchParamsToQueryString(newSearchParams) });
          }}
          onClear={() => history.push({ search: '' })}
          hasConditions={hasConditions}
        />
        {loading ? (
          <LoadingPage />
        ) : !data?.client ? (
          <div className="l-wrap-xs u-pt100">
            <div className="l-box-message">
              <p className="m-title-sub u-pt0 u-ta-c">報告書を取得できませんでした。</p>
            </div>
          </div>
        ) : (
          <Table className={styles.table}>
            <thead>
              <tr>
                <th>報告書名</th>
                <th>適用事業所</th>
                <th className={styles.periodColumn}>届出期間</th>
                <th className={styles.actionsColumn}>アクション</th>
              </tr>
            </thead>
            <tbody>
              {data.client.regularHealthCheckupReports.list.map(report => (
                <tr key={report.id} className="table-hover u-pos-r">
                  <td>
                    <Link className={styles.stretchedLink} to={`/regular_health_checkup/reports/${report.id}`}>
                      {displayFormat(report.title)}
                    </Link>
                  </td>
                  <td>{displayFormat(report.officeSnapshot?.name)}</td>
                  <td className={styles.periodColumn}>{dateSpanFormat(report.startOn, report.endOn)}</td>
                  <td className={styles.actionsColumn}>
                    <Description className={styles.iconContainer} text="編集">
                      <Link className={styles.icon} to={`/regular_health_checkup/reports/${report.id}/edit`}>
                        <Edit size={20} />
                      </Link>
                    </Description>
                    <Description className={styles.iconContainer} text="複製">
                      <Link className={styles.icon} to={`/regular_health_checkup/reports/${report.id}/copy`}>
                        <Copy size={20} />
                      </Link>
                    </Description>
                    <Description className={styles.iconContainer} text="削除">
                      <DeleteReport className={styles.icon} report={report} refetch={refetch} />
                    </Description>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <PaginatorWithResult
          onClick={(page: number) =>
            history.push({ search: convertSearchParamsToQueryString({ ...searchParams, _page: String(page) }) })
          }
          limit={limit}
          count={data?.client?.regularHealthCheckupReports?.totalCount}
          page={searchParams._page}
          isLoading={loading}
          failureMessage="報告書が見つかりませんでした"
        />
      </Content>
    </div>
  );
};

interface DescriptionProps extends ComponentPropsWithoutRef<'div'> {
  text: ReactNode;
  children: ReactNode;
}

const Description: FC<DescriptionProps> = ({ text, children, ...rest }) => {
  const [isHover, setIsHover] = useState(false);

  return (
    <DescriptionContainer {...rest} onMouseEnter={() => setIsHover(true)} onMouseLeave={() => setIsHover(false)}>
      {children}
      <HoverDescription isHover={isHover} position="top" margin="32px">
        <span className={styles.bold}>{text}</span>
      </HoverDescription>
    </DescriptionContainer>
  );
};
